<template>
    <div class="page-container" :class="{ collapsed: !$store.getters.sidebarOpened }">
        <div class="area-container left-area">
            <CommonTree
                ref="faculty_tree"
                type="room"
                :showFilter="true"
                @change="handleNodeClick"
                :customStyle="{ height: 'calc(100vh - 158px)', borderRight: 'none', width: '100%' }"
            />
        </div>
        <div class="data-container center-area">
            <div class="tool-box">
                <el-button type="primary" :icon="Refresh" @click="resetQueryForm">刷新</el-button>
                <el-button
                    type="primary"
                    v-permission="['thermostat:device-manage:add']"
                    :icon="Plus"
                    plain
                    @click="onAddClick()"
                >
                    添加
                </el-button>
            </div>
            <el-table
                class="data-table"
                :data="table_data"
                v-loading="tableLoading"
                @selection-change="handleSelectionChange"
                scrollbar-always-on
            >
                <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
                <el-table-column label="位置" prop="location" align="center" min-width="350">
                    <template #default="{ row }">
                        {{ row.location.join(' / ') || '-' }}
                    </template>
                </el-table-column>
                <el-table-column label="计费方式" prop="method_name" align="center" min-width="150">
                    <template #default="{ row }">
                        <div style="display: flex; flex-direction: row; justify-content: center">
                            <el-tag :type="row.method === 1 ? 'success' : row.method === 2 ? 'warning' : 'primary'">
                                {{ row.method_name }}
                            </el-tag>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column
                    label="热量表(总表)"
                    prop="heat_meter_imei"
                    show-overflow-tooltip
                    min-width="220"
                ></el-table-column>
                <el-table-column label="热量表单价" prop="heat_unit_price" align="center" min-width="150">
                    <template #default="{ row }">
                        {{ row.heat_unit_price ? row.heat_unit_price + ' 元/千瓦时' : '-' }}
                    </template>
                </el-table-column>
                <el-table-column label="温控器(总控)" prop="thermostat_imei" align="center" min-width="220"></el-table-column>
                <el-table-column label="运行时单价" prop="unit_price" align="center" min-width="150">
                    <template #default="{ row }">
                        {{ row.unit_price ? row.unit_price + ' 元/小时' : '-' }}
                    </template>
                </el-table-column>
                <el-table-column label="基础费用" prop="basic_fee" align="center" min-width="150">
                    <template #default="{ row }">
                        {{ row.basic_fee ? row.basic_fee + ' 元' : '-' }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" fixed="right" width="180">
                    <template #default="{ row }">
                        <el-button
                            type="primary"
                            link
                            :icon="Edit"
                            @click="onEditClick(row)"
                            v-permission="['thermostat:device-manage:edit']"
                        >
                            编辑
                        </el-button>
                        <el-button
                            type="primary"
                            :icon="Delete"
                            link
                            @click="onRemoveClick(row.building)"
                            v-permission="['thermostat:device-manage:remove']"
                        >
                            删除
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
            <Pagination
                v-if="table_data.length > 0"
                :current-page="page_params.page"
                :page-size="page_params.page_size"
                :total="total"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
            />
            <FeePlanForm ref="feePlanRef" @success="getTableData" />
        </div>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref, nextTick } from 'vue'
import { Delete, Edit, Plus, Refresh } from '@element-plus/icons'
import { getBillingList, deleteBilling } from '@/api/thermostat/billing.js'
import FeePlanForm from './components/forms.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import Pagination from '@/components/Pagination/index.vue'
import CommonTree from '@/components/BuildTree/CommonTree.vue'

// 计费方式映射
const methodMap = {
    1: { name: '热量表', type: 'success' },
    2: { name: '热量表+温控器', type: 'warning' },
    3: { name: '温控器', type: 'primary' }
}

// 左侧区域树
const faculty_tree = ref(null)
const current_node = ref({})

const handleNodeClick = (data) => {
    current_node.value = data
    resetQueryForm()
}

// 重置
const resetQueryForm = () => {
    page_params.page = 1
    getTableData()
}

const table_data = ref([])
const tableLoading = ref(true)
const total = ref(0)

// 获取列表数据
async function getTableData() {
    if (!current_node.value?.id) {
        table_data.value = []
        total.value = 0
    }

    tableLoading.value = true
    const params = {
        ...page_params,
        building: current_node.value?.id
    }
    try {
        const res = await getBillingList(params)
        // 处理数据，添加显示名称
        table_data.value = (res?.lists || []).map((item) => ({
            ...item,
            method_name: methodMap[item.method]?.name || item.method,
            heat_meter_name: item.heat_meter_name || item.heat_meter || '-',
            thermostat_name: item.thermostat_name || item.thermostat || '-'
        }))
        total.value = res?.total || 0
        tableLoading.value = false
    } catch (err) {
        console.log(err)
        tableLoading.value = false
    }
}

// 分页相关
const page_params = reactive({
    page: 1,
    page_size: 10
})
const handleSizeChange = (currentSize) => {
    page_params.page_size = currentSize
    page_params.page = 1
    getTableData()
}
const handleCurrentChange = (currentPage) => {
    page_params.page = currentPage
    getTableData()
}

// 表格多选
const selected_ids = ref([])
function handleSelectionChange(list) {
    selected_ids.value = list.map((item) => item.id)
}

const feePlanRef = ref(null)

// 添加
const onAddClick = () => {
    if (!current_node.value?.id) {
        ElMessage.warning('请先选择小区节点')
        return
    }
    feePlanRef.value.open({ building: current_node.value.id })
}

// 编辑
const onEditClick = (row) => {
    feePlanRef.value.open(row)
}

// 删除
function onRemoveClick(building) {
    if (!building) {
        ElMessage.error('缺少小区ID，无法删除')
        return
    }
    ElMessageBox.confirm('是否确认删除该计费方式？', '提示', {
        confirmButtonText: '确认',
        cancelButtonText: '取消',
        type: 'warning'
    })
        .then(() => {
            deleteBilling(building)
                .then(() => {
                    ElMessage.success('删除成功')
                    getTableData()
                })
                .catch((err) => {
                    console.log(err)
                    ElMessage.error('删除失败')
                })
        })
        .catch(() => {})
}

// 切换到前台时候执行
onMounted(() => {
    getTableData()
})
</script>

<style scoped>
.page-container {
    display: flex;
    height: 100%;
}
.area-container {
    flex-shrink: 0;
}
.left-area {
    width: 260px;
    border-right: 1px solid #e4e7ed;
}
.center-area {
    flex: 1;
    padding: 16px;
    overflow: auto;
}
.tool-box {
    margin-bottom: 16px;
}
.data-table {
    margin-bottom: 16px;
}
</style>
